import React from "react";
import { useTranslation } from "react-i18next";
import { GroupCommandProvider } from "src/providers/GroupCommandProvider";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useDispatch, useSelector } from "src/store";
import { usePrefetchCurrentWorkspaceEvents } from "src/store/api/events/helpers";

import { selectGroups } from "@dashboard/groups/store/selectors";
import CommandsTab from "@dashboard/shared/components/SidePanel/GroupPanel/CommandsTab/CommandsTab";
import { closeDetails, selectDetailsGroup, selectPaneTab, setPaneTab } from "@devices/store";
import { TabContext, TabList } from "@mui/lab";
import { Box, Typography } from "@mui/material";
import { getSettings } from "@services/configService";
import { SecondaryButton } from "@shared/CustomButton";

import { PaneElement } from "../shared/PaneElement";
import { PaneTabPanel } from "../shared/PaneTabPanel";
import { useStyle } from "../shared/style";
import { Tab } from "../shared/Tab";

import EnrollmentTab from "./EnrollmentTab/EnrollmentTab";
import EventTab from "./EventsTab/EventTab";
import FileTab from "./FileTab/FileTab";
import MemberTab from "./MemberTab/MemberTab";
import MenuGroup from "./MenuGroup/MenuGroup";
import OverviewTab from "./OverviewTab/OverviewTab";
import PolicyTab from "./PolicyTab/PolicyTab";

const groupTabs = ["OVERVIEW", "MEMBERS", "FILES", "ENROLLMENT", "POLICY", "COMMANDS", "EVENTS"];

export const GroupSidePanel = () => {
    const { t } = useTranslation(["sidepanels", "common"]);
    const dispatch = useDispatch();
    const { classes } = useStyle();
    const { features } = getSettings();
    const { effectiveWorkspaceSettings } = useAppSettingsService();
    const filesTabEnabled = features.groupPanel.filesTabEnabled && effectiveWorkspaceSettings.isFilesPageEnabled;

    const group = useSelector(selectDetailsGroup);
    const value = useSelector(selectPaneTab);
    const allGroups = useSelector(selectGroups);

    const isGroupTab = groupTabs.includes(value);

    const onChange = (_event: React.ChangeEvent<object>, tabTitle: (typeof groupTabs)[number]) => {
        dispatch(setPaneTab(tabTitle));
    };

    React.useEffect(() => {
        if (!isGroupTab) {
            dispatch(setPaneTab("OVERVIEW"));
        }
    }, [isGroupTab, dispatch]);

    const prefetchEvents = usePrefetchCurrentWorkspaceEvents();
    const eventsEnabled = features.groupPanel.eventsTabEnabled;
    const groupId = group?.id;
    const allWorkspaceGroupsLength = allGroups.length;

    React.useEffect(() => {
        if (eventsEnabled && groupId) {
            prefetchEvents({ groupIds: [groupId], allWorkspaceGroupsLength });
        }
    }, [eventsEnabled, groupId, allWorkspaceGroupsLength, prefetchEvents]);

    if (!isGroupTab || !group) {
        return null;
    }

    return (
        <GroupCommandProvider>
            <TabContext value={value}>
                <PaneElement.HeaderBox>
                    <Box>
                        <Typography variant="h6" className={classes.panelTitle}>
                            {group.name}
                        </Typography>
                        {group.description && group.description.trim() ? (
                            <Typography variant="body1" className={classes.panelSubtitle}>
                                {group.description}
                            </Typography>
                        ) : null}
                    </Box>

                    <Box className={classes.buttonBox}>
                        <MenuGroup {...group} />
                        <SecondaryButton onClick={() => dispatch(closeDetails())}>
                            <i className="fas fa-times" />
                        </SecondaryButton>
                    </Box>
                </PaneElement.HeaderBox>

                <TabList onChange={onChange} variant="scrollable" scrollButtons>
                    <Tab
                        value="OVERVIEW"
                        label={t("sidepanels:groupPanel.overviewTab.title")}
                        icon={<i className="fas fa-tachometer-alt" />}
                    />
                    {features.groupPanel.membersTabEnabled ? (
                        <Tab
                            value="MEMBERS"
                            label={t("sidepanels:groupPanel.membersTab.title")}
                            icon={<i className="fas fa-users" />}
                        />
                    ) : null}
                    {filesTabEnabled ? (
                        <Tab
                            value="FILES"
                            label={t("sidepanels:groupPanel.filesTab.title")}
                            icon={<i className="fas fa-file" />}
                        />
                    ) : null}
                    {features.groupPanel.enrollmentTabEnabled ? (
                        <Tab
                            value="ENROLLMENT"
                            label={t("sidepanels:groupPanel.enrollmentTab.title")}
                            icon={<i className="fas fa-qrcode" />}
                        />
                    ) : null}
                    {features.groupPanel.policyTabEnabled ? (
                        <Tab
                            value="POLICY"
                            label={t("sidepanels:groupPanel.policyTab.title")}
                            icon={<i className="fa-solid fa-gears" />}
                        />
                    ) : null}
                    {features.groupPanel.eventsTabEnabled ? (
                        <Tab value="EVENTS" label={t("common:label.events")} icon={<i className="fa-solid fa-bell" />} />
                    ) : null}
                    {features.groupPanel.commandsTabEnabled ? (
                        <Tab
                            value="COMMANDS"
                            label={t("sidepanels:groupPanel.commandsTab.title")}
                            icon={<i className="fa-solid fa-terminal" />}
                        />
                    ) : null}
                </TabList>

                <Box className={classes.contentBox}>
                    <PaneTabPanel value="OVERVIEW">
                        <OverviewTab {...group} />
                    </PaneTabPanel>
                    {features.groupPanel.membersTabEnabled ? (
                        <PaneTabPanel value="MEMBERS" fill>
                            <MemberTab {...group} />
                        </PaneTabPanel>
                    ) : null}
                    {filesTabEnabled ? (
                        <PaneTabPanel value="FILES" fill>
                            <FileTab {...group} />
                        </PaneTabPanel>
                    ) : null}
                    {features.groupPanel.enrollmentTabEnabled ? (
                        <PaneTabPanel value="ENROLLMENT">
                            <EnrollmentTab {...group} />
                        </PaneTabPanel>
                    ) : null}
                    {features.groupPanel.policyTabEnabled ? (
                        <PaneTabPanel value="POLICY" fill>
                            <PolicyTab {...group} />
                        </PaneTabPanel>
                    ) : null}
                    {features.groupPanel.commandsTabEnabled ? (
                        <PaneTabPanel value="COMMANDS" fill>
                            <CommandsTab group={group} />
                        </PaneTabPanel>
                    ) : null}
                    {features.groupPanel.eventsTabEnabled && (
                        <PaneTabPanel value="EVENTS" fill>
                            <EventTab {...group} />
                        </PaneTabPanel>
                    )}
                </Box>
            </TabContext>
        </GroupCommandProvider>
    );
};
